import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import daCommon from './locales/da/common.json';
import enCommon from './locales/en/common.json';
import noCommon from './locales/no/common.json';

export const LANGUAGE_STORAGE_KEY = 'webapp.language';
export const SUPPORTED_LANGUAGES = ['no', 'da', 'en'] as const;
const SUPPORTED_LANGUAGE_SET = new Set(SUPPORTED_LANGUAGES);

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

function isSupportedLanguage(value: string): value is AppLanguage {
  return SUPPORTED_LANGUAGE_SET.has(value as AppLanguage);
}

export function normalizeLanguage(value?: string | null): AppLanguage | null {
  if (!value) {
    return null;
  }

  const normalizedValue = value.toLowerCase();

  if (
    normalizedValue === 'no' ||
    normalizedValue === 'no-no' ||
    normalizedValue === 'nb' ||
    normalizedValue === 'nb-no' ||
    normalizedValue === 'nn' ||
    normalizedValue === 'nn-no'
  ) {
    return 'no';
  }

  if (normalizedValue === 'da' || normalizedValue === 'da-dk') {
    return 'da';
  }

  if (normalizedValue === 'en' || normalizedValue.startsWith('en-')) {
    return 'en';
  }

  return null;
}

function getSavedLanguage(): AppLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return normalizeLanguage(savedLanguage);
}

function getNavigatorLanguage(): AppLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const navigatorLanguages = window.navigator.languages ?? [];
  const candidates = [...navigatorLanguages, window.navigator.language];

  for (const candidate of candidates) {
    const normalizedLanguage = normalizeLanguage(candidate);
    if (normalizedLanguage) {
      return normalizedLanguage;
    }
  }

  return null;
}

export function getPreferredLanguage(
  fallbackLanguage: AppLanguage = 'no'
): AppLanguage {
  return getSavedLanguage() ?? getNavigatorLanguage() ?? fallbackLanguage;
}

function getInitialLanguage() {
  return getPreferredLanguage('no');
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: {
      no: { translation: noCommon },
      da: { translation: daCommon },
      en: { translation: enCommon },
    },
    lng: getInitialLanguage(),
    fallbackLng: 'no',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false,
    },
  });
}

if (typeof window !== 'undefined') {
  i18n.on('languageChanged', (language) => {
    const normalizedLanguage = normalizeLanguage(language);
    if (!normalizedLanguage || !isSupportedLanguage(normalizedLanguage)) {
      return;
    }
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
    document.documentElement.lang = normalizedLanguage;
  });

  document.documentElement.lang =
    normalizeLanguage(i18n.resolvedLanguage || i18n.language) || 'no';
}

export default i18n;
